import React, { useState } from 'react';
import {
  VStack, HStack, Text, Badge, BadgeText, Pressable,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useEventCalendarData } from '../hooks/useEventCalendarData';
import { groupTasksByModule } from '../utils/slotUtils';

/**
 * SlotTasksSection Component - Displays tasks bound to a slot, grouped by module
 */
const SlotTasksSection = ({ boundTasks = [], boundTaskIds = [] }) => {
  const { t } = useTranslation();
  const { taskMap, moduleMap } = useEventCalendarData();
  const [showAll, setShowAll] = useState(false);

  const groupedTasks = React.useMemo(() => {
    if (Array.isArray(boundTasks) && boundTasks.length > 0) {
      const grouped = {};
      boundTasks.forEach((bt) => {
        if (!bt) return;
        const moduleTitle = bt.moduleTitle || moduleMap[bt.moduleId] || bt.moduleId;
        if (!grouped[moduleTitle]) grouped[moduleTitle] = [];
        grouped[moduleTitle].push({ id: bt.taskId, title: bt.taskTitle || taskMap[bt.taskId]?.title || bt.taskId });
      });
      return grouped;
    }
    return groupTasksByModule(boundTaskIds, taskMap, moduleMap);
  }, [boundTasks, boundTaskIds, taskMap, moduleMap]);

  const hasBoundTasks = Array.isArray(boundTasks) && boundTasks.length > 0;
  const hasBoundTaskIds = Array.isArray(boundTaskIds) && boundTaskIds.length > 0;
  if (!hasBoundTasks && !hasBoundTaskIds) {
    return null;
  }

  const entries = Object.entries(groupedTasks);
  const visibleEntries = showAll ? entries : entries.slice(0, 2);
  const hasMore = entries.length > 2;

  return (
    <VStack>
      <Text size="2xs" mb="$1" color="$textSubtle">
        {t('lecturer.eventCalendar.sidebar.sections.modules')}
      </Text>
      {visibleEntries.map(([module, tasks]) => (
        <VStack key={module} mb="$2">
          <Badge size="md" variant="solid" bg="$backgroundLight200" mb="$1" alignSelf="flex-start">
            <BadgeText size="sm" color="$textHeading" m="$0.5" maxWidth={250} numberOfLines={1}>
              {module}
            </BadgeText>
          </Badge>
          <HStack flexWrap="wrap" gap="$2" pl="$6">
            {tasks.map((task) => {
              // Skip trivial task names
              if (task.title === 'Group Study Session') return null;
              return (
                <Badge key={task.id} size="sm" variant="solid" bg="$backgroundLight100">
                  <BadgeText size="xs" color="$textBody" m="$0.5">
                    {task.title}
                  </BadgeText>
                </Badge>
              );
            })}
          </HStack>
        </VStack>
      ))}

      {hasMore && (
        <Pressable
          testID="lecturer-calendar-slot-modules-more"
          onPress={() => setShowAll(!showAll)}
        >
          <Text size="xs" color="$primary600" mt="$1" fontWeight="semibold">
            {showAll
              ? t('common.buttons.show-less')
              : t('lecturer.eventCalendar.sidebar.moreModules', { moreCount: entries.length - 2 })}
          </Text>
        </Pressable>
      )}
    </VStack>
  );
};

export default SlotTasksSection;
